import { httpClient, initializeApiClient, ApiError } from "../services/httpClient";
import type { FreeRewriteStatus } from "../types/freeRewrite";

export const initializeApiHelpers = initializeApiClient;

// Функция для парсинга content-disposition заголовка
export function parseContentDisposition(contentDisposition: string): string | null {
  if (!contentDisposition) return null;

  // Ищем filename* (RFC 5987) - приоритетный формат
  const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (filenameStarMatch) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch (e) {
      console.warn('Ошибка декодирования filename*:', e);
    }
  }

  // Ищем обычный filename
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (filenameMatch) {
    let filename = filenameMatch[1];

    // Проверяем на RFC 2047 encoding (=?charset?encoding?encoded-text?=)
    const rfc2047Match = filename.match(/^=\?([^?]+)\?([BQ])\?([^?]+)\?=$/i);
    if (rfc2047Match) {
      const [, , encoding, encodedText] = rfc2047Match;
      try {
        if (encoding.toUpperCase() === 'B') {
          // Base64 decoding
          const decoded = atob(encodedText);
          return decoded;
        } else if (encoding.toUpperCase() === 'Q') {
          // Quoted-printable decoding (упрощенная версия)
          const decoded = encodedText.replace(/=([0-9A-F]{2})/g, (match, hex) =>
            String.fromCharCode(parseInt(hex, 16))
          ).replace(/_/g, ' ');
          return decoded;
        }
      } catch (e) {
        console.warn('Ошибка декодирования RFC 2047:', e);
      }
    }

    return filename;
  }

  return null;
}

const buildApiError = async (response: Response) => {
  let message = `Request failed with status ${response.status}`;
  try {
    const data = await response.clone().json();
    if (data?.message) message = data.message;
    return new ApiError(message, response.status, data);
  } catch {
    try {
      const text = await response.text();
      if (text) message = text;
    } catch {}
  }
  return new ApiError(message, response.status);
};

const ensureOk = async (response: Response) => {
  if (response.ok) return;
  throw await buildApiError(response);
};

export async function fetchWithAuth(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  return httpClient.fetch(input, init);
}

export async function deleteDocument(
  opts: { slotId: string } | { all: true }
): Promise<{ success: boolean; message: string; data: any }> {
  if ("slotId" in opts) {
    return httpClient.request<{ success: boolean; message: string; data: any }>(
      `/api/documents?slotId=${encodeURIComponent(opts.slotId)}`,
      {
        method: "DELETE",
      }
    );
  }

  return httpClient.request<{ success: boolean; message: string; data: any }>(
    "/api/documents?all=true",
    {
      method: "DELETE",
    }
  );
}

export interface JobStatus {
  id: string;
  state: 'failed' | 'running' | 'succeeded' | 'active';
  progress: number;
  queue: 'parse' | 'paraphrase' | 'antiplagiat' | 'attach-report' | 'attach-report-from-ap' | 'prepare-antiplagiat-doc' | 'attach-antiplagiat-report';
  docNumber: number;
  slotId: string;
  mode?: string;
  createdAt: number;
  processedOn?: number;
  finishedOn?: number;
  failedReason?: string;
  /** Машиночитаемый код ошибки (PARSER_ERRORS_FRONTEND.md §4). */
  errorCode?: string;
  /** Можно ли предложить «Повторить» без перезагрузки DOCX. */
  recoverable?: boolean;
  /** Диагностика ошибки. */
  errorDetails?: any;
}

export interface JobStatusResponse {
  success: boolean;
  data: {
    hasActiveJob: boolean;
    jobs: JobStatus[];
  };
}

export async function fetchJobStatus(): Promise<JobStatusResponse> {
  return httpClient.request<JobStatusResponse>('/files/job-status', {
    method: 'GET',
  });
}

export interface DocumentStatusFlags {
  uploaded: boolean;
  parsed: boolean;
  paraphrased: boolean;
  built: boolean;
}

/** Статус проверки версии (см. REWRITE_CHECK_FRONTEND.md, раздел 3). */
export type VersionCheckStatus = 'none' | 'pending' | 'processing' | 'ready' | 'failed';

export interface VersionCheck {
  /** id отчёта проверки этой версии; null = не проверялась. */
  reportId: string | null;
  /** Версия уже отправлялась на проверку. */
  checked: boolean;
  status: VersionCheckStatus;
  /** Отчёт готов (есть проценты). */
  isReady: boolean;
  checkProvider?: 'antiplagiat_ru' | 'domate';
  originalityPercent?: number | null;
  aiPercent?: number | null;
  aiContentAbsent?: boolean;
}

export interface ParsedVersion {
  docNumber: number;
  fileName: string;
  lastModified: string;
  size: number;
  totalPrice: number;
  totalPriceAI: number;
  totalPriceWithReport: number;
  totalPriceAIWithReport: number;
  reportId: string | null
  type: 'original' | 'paraphrased' | 'intermediate';
  /** true — обычный пользователь может отправить версию на проверку. */
  isLastVersion?: boolean;
  /** Статус проверки версии уникальности (новая архитектура). */
  check?: VersionCheck;
}

export interface DocumentStatusResponse {
  success: boolean;
  data: {
    slotId: string;
    hasDocument: boolean;
    documentStatus: 'empty' | 'uploaded' | 'parsed' | 'paraphrased' | 'built';
    statusFlags: DocumentStatusFlags;
    documentName?: string;
    parsedVersions: ParsedVersion[];
    totalVersions: number;
    lastModified?: string;
    extractedFilesCount?: number;
    /** Пайплайн «убрать ИИ» (README_REMOVE_AI_PIPELINE_FRONTEND.md). */
    removeAi?: { target: "antiplagiat" | "domate" | null; locked: boolean };
    // Статус документа в antiplagiat/ (новая архитектура)
    antiplagiat?: {
      hasDocument: boolean;
      isParsed: boolean;
      hasAiReport: boolean;
      hasCoincidencesReport: boolean;
      originalName?: string;
    };
  };
}

export async function fetchDocumentStatus(slotId: string): Promise<DocumentStatusResponse> {
  return httpClient.request<DocumentStatusResponse>(`/files/status?slotId=${encodeURIComponent(slotId)}`, {
    method: 'GET',
  });
}

export interface RewriteSlot {
  id: string;
  title: string | null;
  filesPresent: boolean;
  storagePurgedAt: string | null;
  createdAt: string;
  updatedAt: string;
  hasDocument: boolean;
  documentStatus: "empty" | "uploaded" | "parsed" | "paraphrased" | "built";
  statusFlags: DocumentStatusFlags;
  rewriteKind: "empty" | "original_only" | "has_rewrite";
  totalVersions: number;
}

export interface RewriteSlotsResponse {
  success: boolean;
  data: RewriteSlot[];
}

export async function fetchRewriteSlots(): Promise<RewriteSlotsResponse> {
  return httpClient.request<RewriteSlotsResponse>("/files/slots", {
    method: "GET",
  });
}

export async function setActiveRewriteSlot(slotId: string): Promise<{ success: boolean; message?: string }> {
  return httpClient.request<{ success: boolean; message?: string }>("/files/active-slot", {
    method: "POST",
    body: JSON.stringify({ slotId }),
  });
}

// Статус обработки документа
export type DocumentProcessingStatus = 'pending' | 'processing' | 'ready' | 'failed';

export type Report = {
  id: string;
  isReady: boolean;
  documentName: string | null;
  createdAt: string; // ISO
  updatedAt: string; // ISO
  
  // Новые поля для новой архитектуры
  rewritePrice?: number;        // Цена рерайта (после готовности документа)
  rewritePriceAI?: number;      // Цена рерайта в режиме ИИ (после готовности документа)
  originalityPercent?: number;  // Процент оригинальности (после готовности отчета)
  aiPercent?: number;           // Процент ИИ (после готовности отчета)
  documentAvailable: boolean;   // Доступен ли документ для отправки на рерайт
  documentProcessingStatus: DocumentProcessingStatus;  // Статус обработки документа
  documentProcessingError?: string;  // Ошибка обработки документа
  /** Слот рерайта, если документ уже связан с сессией рерайта */
  sentToRewriteSlotId?: string | null;
  /** Провайдер проверки: antiplagiat_ru | domate */
  checkProvider?: "antiplagiat_ru" | "domate" | string | null;
  /** Ссылка на отчёт Domate в браузере */
  reportOpenUrl?: string | null;
  /** Относительный путь к веб-отчёту у провайдера (если бэкенд отдаёт) */
  providerWebReportPath?: string | null;
  /** Фактически списанная сумма за проверку (₽) */
  checkChargedAmount?: number | null;
};

export type ReportData = {
  aiScore: number;
  aiPages: number[];
  plagiarism: number;
  legal: number;
  documentName: string;
  selfcite: number;
  sources: {
    id: number;
    type: "Legal" | "Plagiarism" | "Selfcite";
    percentageByReport: number;
    percentageBySource: number;
    name: string;
    link: string;
  }[];
};

export type ReportFiles = {
  reportData: ReportData | null;
  originalityPdfUrl: string | null; // ссылка на PDF «Оригинальность» (data:application/pdf;base64,...)
  suspiciousPdfUrl: string | null; // ссылка на PDF «Подозрительный документ»
  warnings: string[]; // причины частичных сбоев от бэкенда
  checkProvider?: "antiplagiat_ru" | "domate" | string | null;
  reportOpenUrl?: string | null;
};

export async function fetchReports({
  page,
  limit,
  query,
}: {
  page: number;
  limit: number;
  query: string;
}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));
  if (query) params.set("q", query);

  const res = await httpClient.fetch(`plagiarism/reports?${params.toString()}`, {
    method: "GET",
  });
  await ensureOk(res);

  const data: Report[] = await res.json();

  // Try to get the total count from headers if backend provides it.
  const totalHeader = res.headers?.get?.("X-Total-Count");
  const total = totalHeader ? Number(totalHeader) : undefined;

  return { data, total };
}

/**
 * GET /plagiarism/reports/:id
 *
 * Бэкенд возвращает стабильный JSON со статусами:
 *   200 — всё успешно,
 *   207 — частичный успех (какие-то поля null, подробности в `warnings`),
 *   502 — ничего не удалось (в т.ч. `data === null`).
 *
 * На фронте важно ориентироваться на наличие `data`, а не только на статус:
 * если `data` пришла — отчёт уже можно отрисовать, даже если PDF пустые.
 */
export async function fetchReportFiles(reportId: string): Promise<ReportFiles> {
  const res = await httpClient.fetch(`plagiarism/reports/${reportId}`, {
    method: "GET",
  });

  // Пытаемся распарсить JSON в любом случае — бэкенд всегда пишет структурированный ответ.
  let json: any = null;
  try {
    json = await res.clone().json();
  } catch {}

  // Статус 502 и другие ошибки: если удалось распарсить тело — используем его warnings/error,
  // иначе падаем в стандартную обработку ошибок.
  if (!res.ok && res.status !== 207) {
    const warnings: string[] = Array.isArray(json?.warnings) ? json.warnings : [];
    const message =
      (typeof json?.error === "string" && json.error) ||
      warnings[0] ||
      json?.message ||
      `Request failed with status ${res.status}`;
    const err = new ApiError(message, res.status, { ...(json || {}), warnings });
    throw err;
  }

  // 200 / 207 — рабочий контракт. `data` может быть null при 502, но туда мы уже не зайдём.
  const warnings: string[] = Array.isArray(json?.warnings) ? json.warnings : [];

  return {
    reportData: json?.data ?? null,
    originalityPdfUrl: json?.originality ?? null,
    suspiciousPdfUrl: json?.ai ?? null,
    warnings,
    checkProvider: json?.checkProvider ?? null,
    reportOpenUrl: json?.reportOpenUrl ?? null,
    // Пробрасываем reportMeta, если бэкенд его добавит (обратная совместимость).
    ...(json?.reportMeta ? { reportMeta: json.reportMeta } : {}),
  } as ReportFiles;
}

// Ответ от POST /plagiarism/send
export interface SendToAntiplagiatResponse {
  success: boolean;
  message: string;
  jobId: string;           // ID задачи проверки на антиплагиат
  prepareJobId: string;    // ID задачи подготовки документа
  report: Report;
}

export async function sendToAntiplagiat(
  file: File,
  ignoreBibliography: boolean,
  checkProvider: "antiplagiat_ru" | "domate" = "antiplagiat_ru"
): Promise<SendToAntiplagiatResponse> {
  const formData = new FormData();
  formData.append("docx", file);

  const qs = new URLSearchParams({
    ignoreBibliography: String(ignoreBibliography),
    checkProvider,
  });

  const json = await httpClient.request<SendToAntiplagiatResponse>(
    `plagiarism/send?${qs.toString()}`,
    {
      method: "POST",
      body: formData,
    }
  );

  return json;
}

// ------------------- Проверка версии со страницы рерайта -------------------
// (см. REWRITE_CHECK_FRONTEND.md)

/** Цена проверки с учётом скидки для одного провайдера. */
export interface RewriteCheckProviderPricing {
  basePrice: number;
  discountPercent: number;
  finalPrice: number;
  discountAmount: number;
  balanceReason?: string;
}

/** Готовые цены проверки по провайдерам из GET /profile/me. */
export interface RewriteCheckPricing {
  antiplagiat_ru?: RewriteCheckProviderPricing;
  domate?: RewriteCheckProviderPricing;
}

/**
 * Цена проверки с учётом типа версии (см. REWRITE_CHECK_FRONTEND.md, раздел 4):
 * скидка действует только на результаты рерайта (`paraphrased` / `intermediate`).
 * Исходный документ (`original`) проверяется по полной цене — `basePrice`, без скидки.
 * Бэкенд считает так же, и `pricing` в ответе запуска равен фактически списанной сумме.
 */
export function pricingForVersionType(
  pricing: RewriteCheckProviderPricing,
  versionType: ParsedVersion["type"] | undefined
): RewriteCheckProviderPricing {
  if (versionType !== "original") return pricing;
  return {
    ...pricing,
    discountPercent: 0,
    finalPrice: pricing.basePrice,
    discountAmount: 0,
  };
}

export interface CheckRewriteParams {
  /** Номер версии в слоте (document-N). */
  docNumber: number;
  checkProvider: "antiplagiat_ru" | "domate";
  /** Слот. Если не передан — берётся активный слот пользователя. */
  slotId?: string;
  /** Не учитывать список литературы (по умолчанию false). */
  ignoreBibliography?: boolean;
}

export interface CheckRewriteResponse {
  success: boolean;
  message: string;
  jobId: string;
  prepareJobId: string;
  slotId: string;
  docNumber: number;
  pricing: RewriteCheckProviderPricing;
  report: Report;
}

/**
 * POST /plagiarism/check-rewrite — запуск проверки версии прямо со страницы
 * рерайта (сервер сам собирает .docx из распарсенной версии слота).
 */
export async function checkRewriteVersion(
  params: CheckRewriteParams
): Promise<CheckRewriteResponse> {
  return httpClient.request<CheckRewriteResponse>("plagiarism/check-rewrite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      docNumber: params.docNumber,
      checkProvider: params.checkProvider,
      ...(params.slotId ? { slotId: params.slotId } : {}),
      ...(typeof params.ignoreBibliography === "boolean"
        ? { ignoreBibliography: params.ignoreBibliography }
        : {}),
    }),
  });
}

// Ответ от GET /plagiarism/can-upload
export interface CanUploadResponse {
  canUpload: boolean;
  reason?: string;           // Причина, если нельзя
  pendingReportId?: string;  // ID незавершенного отчета
}

export async function checkCanUpload(): Promise<CanUploadResponse> {
  return httpClient.request<CanUploadResponse>('plagiarism/can-upload', {
    method: 'GET',
  });
}

// Режим перефразирования
export type RewriteMode = 'default' | 'ai' | 'pro' | 'add-text' | 'remove-generate' | 'english' | 'kazakh';

// Ответ от POST /plagiarism/send-to-rewrite
export interface SendToRewriteResponse {
  success: boolean;
  message: string;
  /** При ошибках вроде rewrite_disabled может приходить вместо message */
  error?: string;
  code?: string;
  slotId: string;
  docNumber?: number;  // Номер документа в rewrite/
  mode?: string;       // Выбранный режим
  /** Документ уже в рерайте; слот переключён */
  alreadySent?: boolean;
  /** При alreadySent: число распаршенных версий в слоте; 1 → «Прикрепить отчёт», >1 или не передано → «Просмотр» */
  parsedVersionCount?: number;
}

export async function sendToRewrite(reportId: string, mode: RewriteMode = 'default'): Promise<SendToRewriteResponse> {
  return httpClient.request<SendToRewriteResponse>('plagiarism/send-to-rewrite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reportId, mode }),
  });
}

/** Путь `/app/:slotId?...` после успешного send-to-rewrite (режим с страницы отчётов — `default`). */
export function rewriteAppPathAfterSendToRewrite(
  result: SendToRewriteResponse,
  mode: RewriteMode = "default"
): string | null {
  if (!result.success || !result.slotId) return null;
  const plagiarismType = mode === "ai" ? "Ai" : "Coincidences";
  const docParam =
    typeof result.docNumber === "number" ? `doc=${result.docNumber}&` : "";
  const qs = `${docParam}mode=${encodeURIComponent(
    String(result.mode || mode)
  )}&plagiarismType=${plagiarismType}`;
  if (result.alreadySent) {
    const n = result.parsedVersionCount;
    if (typeof n === "number" && n === 1) {
      return `/app/${result.slotId}?${qs}&plagiarismOnly=1`;
    }
    return `/app/${result.slotId}?${qs}&rewriteView=1`;
  }
  return `/app/${result.slotId}?${qs}&openRewrite=1`;
}

// Ответ от GET /plagiarism/document-status
export interface AntiplagiatDocumentStatusResponse {
  success: boolean;
  data: {
    exists: boolean;
    originalName?: string;
    parsedPath?: string;
    hasAiReport?: boolean;
    hasCoincidencesReport?: boolean;
  };
}

export async function fetchAntiplagiatDocumentStatus(): Promise<AntiplagiatDocumentStatusResponse> {
  return httpClient.request<AntiplagiatDocumentStatusResponse>('plagiarism/document-status', {
    method: 'GET',
  });
}

/** POST /plagiarism/sync-check-status — ручная подтяжка готовности отчёта (см. ANTIPLAGIAT_SYNC_STATUS_FRONTEND.md) */
export type SyncPlagiarismCheckStatusResponse =
  | { success: true; status: "pending"; state: number }
  | { success: true; status: "completed"; report: Report }
  | { success: true; status: "already_complete"; report: Report };

export async function syncPlagiarismCheckStatus(
  reportId: string
): Promise<SyncPlagiarismCheckStatusResponse> {
  return httpClient.request<SyncPlagiarismCheckStatusResponse>("plagiarism/sync-check-status", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reportId }),
  });
}

// ------------------- Payments & Profile -------------------
export interface PaymentLinkPayload {
  amount: number;
  email: string;
}

export interface PaymentLinkResponse {
  success: boolean;
  message: string;
  data: {
    link: string;
    paymentId: string;
    status: string;
  };
}

export async function createPaymentLink(
  payload: PaymentLinkPayload
): Promise<PaymentLinkResponse> {
  return httpClient.request<PaymentLinkResponse>("/payments/link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export interface PaymentStatusResponse {
  success: boolean;
  message: string;
  data: {
    paymentId: string;
    status: string;
  };
}

export async function fetchPaymentStatus(
  paymentId: string
): Promise<PaymentStatusResponse> {
  return httpClient.request<PaymentStatusResponse>(
    `/payments/status/${paymentId}`,
    {
      method: "GET",
    }
  );
}

export interface PaymentBonusRule {
  from: number;
  to: number;
  percent: number;
}

export interface PaymentBonusesResponse {
  success: boolean;
  data: PaymentBonusRule[];
}

export async function fetchPaymentBonuses(): Promise<PaymentBonusesResponse> {
  return httpClient.request<PaymentBonusesResponse>("/payments/bonuses", {
    method: "GET",
  });
}

export interface UserProfileResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      username: string | null;
      email: string | null;
      balance: number;
      bonus: number;
      totalPromptTokens: number;
      totalCompetitionsTokens: number;
      totalHandledDocument: number;
      isChannelSubscribed: boolean;
      subscribedCounter: number;
      lastBonusUsed: string | null;
      /** Цена за страницу (₽) */
      priceCoefficient: number | null;
      /** Цена проверки на антиплагиат (₽), по умолчанию 250 */
      antiplagiatCheckPrice?: number;
      /** Цена проверки Domate (₽), если бэкенд отдаёт; иначе на фронте — дефолт из конфига */
      domateCheckPrice?: number;
      /** Эффективная скидка (%) на проверку версии со страницы рерайта. */
      rewriteCheckDiscountPercent?: number | null;
      /** Готовые цены проверки версии (с учётом скидки) по провайдерам. */
      rewriteCheckPricing?: RewriteCheckPricing;
      activeRewriteSlotId?: string | null;
      createdAt: string;
      updatedAt: string;
    };
    rewriteSlots?: RewriteSlot[];
    referrals: {
      count: number;
      list: Array<{
        id: string;
        username: string | null;
        email: string | null;
        createdAt: string;
      }>;
    };
    /** Реферальный код пользователя (8 символов) */
    referralCode?: string;
    /** Готовая реферальная ссылка */
    referralLink?: string;
    /** Статус бонуса бесплатного рерайта */
    freeRewrite?: FreeRewriteStatus;
  };
}

export async function fetchUserProfile(): Promise<UserProfileResponse> {
  return httpClient.request<UserProfileResponse>("/profile/me", {
    method: "GET",
  });
}

/** Запись истории операций по балансу (пополнения и списания) */
export interface PaymentHistoryItem {
  id: string;
  direction: "in" | "out";
  amount: number;
  delta: number;
  createdAt: string;
  /** Только для пополнений (direction === 'in') */
  paymentId?: string;
  status?: string;
  bonus?: number;
  depositSource?: "payment" | "admin" | "referral";
  note?: string;
  referredUserId?: string;
  sourceDepositAmount?: number;
  /** Только для списаний (direction === 'out') */
  reason?: "paraphrase" | "antiplagiat" | "psychology" | "chat" | "admin" | string;
  docNumber?: number | null;
  jobId?: string | null;
  reportId?: string | null;
}

export interface PaymentHistoryResponse {
  success: boolean;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    items: PaymentHistoryItem[];
  };
}

export interface HistoryChargeReasonAggregate {
  count: number;
  totalAmount: number;
  totalDelta: number;
}

export interface PaymentHistoryPeriodSummary {
  payment: {
    count: number;
    totalAmount: number;
    totalBonus: number;
    totalDelta: number;
    pendingCount: number;
    pendingAmount: number;
  };
  admin: {
    count: number;
    totalAmount: number;
    totalBonus: number;
    totalDelta: number;
  };
  referral: {
    count: number;
    totalBonus: number;
  };
  charges: {
    count: number;
    totalAmount: number;
    totalDelta: number;
    byReason: Record<string, HistoryChargeReasonAggregate>;
  };
  totals: {
    balanceIn: number;
    balanceOut: number;
    bonusIn: number;
    netBalanceChange: number;
  };
}

export interface PaymentHistoryPeriodData {
  period: { from: string; to: string };
  total: number;
  page: number;
  limit: number;
  summary: PaymentHistoryPeriodSummary;
  items: PaymentHistoryItem[];
}

export interface PaymentHistoryPeriodResponse {
  success: boolean;
  message: string;
  data: PaymentHistoryPeriodData;
}

export async function fetchPaymentHistory(params: { page: number; limit: number }) {
  const query = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  }).toString();
  return httpClient.request<PaymentHistoryResponse>(`/payments/history?${query}`, {
    method: "GET",
  });
}

export async function fetchPaymentHistoryPeriod(params: {
  from?: string;
  to?: string;
  page: number;
  limit: number;
}) {
  const query = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  return httpClient.request<PaymentHistoryPeriodResponse>(
    `/payments/history/period?${query.toString()}`,
    { method: "GET" }
  );
}

// ------------------- Loyalty Program -------------------
export interface LoyaltyTier {
  id: string;
  name: string;
  pricePerPage: number;
}

export interface LoyaltyPeriod {
  start: string;
  end: string;
  totalDeposits: number;
  minutesRemaining: number;
  periodDurationMinutes: number;
}

export interface NextTier {
  id: string;
  name: string;
  pricePerPage: number;
  minDeposit: number;
  depositNeeded: number;
}

export interface TierInfo {
  id: string;
  name: string;
  minDeposit: number;
  maxDeposit: number | null;
  pricePerPage: number;
  isCurrent: boolean;
}

export interface LoyaltyInfo {
  currentTier: LoyaltyTier | null;
  currentPeriod: LoyaltyPeriod;
  nextTier: NextTier | null;
  allTiers: TierInfo[];
}

export interface LoyaltyResponse {
  success: boolean;
  message: string;
  data: LoyaltyInfo;
}

export interface PricingTiersResponse {
  success: boolean;
  message: string;
  data: TierInfo[];
}

export async function fetchLoyaltyInfo(): Promise<LoyaltyResponse> {
  return httpClient.request<LoyaltyResponse>("/payments/loyalty", {
    method: "GET",
  });
}

export async function fetchPricingTiers(): Promise<PricingTiersResponse> {
  return httpClient.request<PricingTiersResponse>("/payments/pricing-tiers", {
    method: "GET",
  });
}
